import { ReactNode, useMemo } from 'react';

import LockIcon from '@/assets/icons/lock-sm';
import { EventCard, EventCardProps } from '@/components/EventCard';
import { useEventTime } from '@/components/EventTime';
import { Row } from '@/components/lib/components';
import { Event } from '@/feature/event-details-sbk/types';
import { getTeamColors } from '@/feature/event-details-sbk/utils/get-team-colors';
import { useFormatOdds } from '@/hooks/use-format-odds';
import { useEventStatus, useTeamScore } from '@/hooks/use-sbk-event-details-cache';
import { getPickemLeague } from '@/utils/league-utils';
import { getTeamOrder } from '@/utils/sport-utils';

export type SbkEventCardProps = {
    event: Event;
    navigateToEvent: (event: Event) => void;
    isSelected?: boolean;
    variant?: EventCardProps['variant'];
};

const SuspendedOdds = () => {
    return (
        <Row alignItems="center" gap="s4" height={20}>
            <LockIcon />
        </Row>
    );
};

export const SbkEventCard = ({ event, navigateToEvent, isSelected = false, variant = 'lobby' }: SbkEventCardProps) => {
    const matchDetails = useEventTime(event);

    const { home: homeColors, away: awayColors } = getTeamColors(event);

    const { formatOdds } = useFormatOdds();

    const teamOrder = getTeamOrder(event.sport.name);
    const [firstSide, secondSide] = teamOrder;

    const teams = {
        away: {
            team: event.away_team,
            primary: awayColors.primary,
            secondary: awayColors.secondary,
            odds: event.away_team_winner_odds,
        },
        home: {
            team: event.home_team,
            primary: homeColors.primary,
            secondary: homeColors.secondary,
            odds: event.home_team_winner_odds,
        },
    };

    const first = teams[firstSide];
    const second = teams[secondSide];

    const odds: [string | ReactNode, string | ReactNode] = useMemo(() => {
        const firstOdds = first.odds === 1 ? <SuspendedOdds /> : formatOdds(first.odds);
        const secondOdds = second.odds === 1 ? <SuspendedOdds /> : formatOdds(second.odds);
        return [firstOdds, secondOdds];
    }, [first.odds, second.odds, formatOdds]);

    const league = getPickemLeague(event);

    const firstScore = useTeamScore(event, firstSide) ?? event.event_details[`${firstSide}_score`];
    const secondScore = useTeamScore(event, secondSide) ?? event.event_details[`${secondSide}_score`];
    const eventStatus = useEventStatus(event.id) ?? event.status;

    return (
        <EventCard
            awayTeamIcon={first.team.logo_url || ''}
            homeTeamIcon={second.team.logo_url || ''}
            awayTeamShortCode={first.team.short_code}
            homeTeamShortCode={second.team.short_code}
            eventTitle={matchDetails}
            awayPrimaryTeamColor={first.primary}
            homePrimaryTeamColor={second.primary}
            awaySecondaryTeamColor={first.secondary}
            homeSecondaryTeamColor={second.secondary}
            awayScore={firstScore}
            homeScore={secondScore}
            league={league}
            isLive={eventStatus === 'LIVE'}
            isFinished={eventStatus === 'FINISHED'}
            ballPossession={event.event_details.sport_specific?.ball_possession}
            teamInfo={odds}
            variant={variant}
            onPress={() => navigateToEvent(event)}
            isSelected={isSelected}
        />
    );
};
